/**
 * Copyright(c) 2022 Fenix Outdoor International AG.
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information of Fenix ("Confidential
 * Information"). You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the contract agreement you entered into with Fenix.
 *
 */

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Pagination from "../core/Pagination";
import PaginationItem from "../core/PaginationItem";
import PaginationLink from "../core/PaginationLink";
import Input from "../core/Input";
import Button from "../core/Button";

const generatePages = (currentPage, totalPages) => {
  const leftOffset = 2;
  const rightOffset = 3;
  const currentPageIn = Number.parseInt(currentPage, 10);
  const totalPagesIn = Number.parseInt(totalPages, 10);
  const paginationStart = currentPageIn - leftOffset;
  const paginationEnd = currentPageIn + rightOffset;
  const rawPaginationArray = Array.from(
    { length: paginationEnd - paginationStart },
    (_, i) => paginationStart + i,
  );
  const carryoverLeft = [];
  const carryoverRight = [];
  const validPagination = [];
  let currentLeftEnd = paginationStart;
  let currentRightEnd = paginationEnd - 1;
  rawPaginationArray.forEach((each) => {
    if (each < 3) {
      if (currentRightEnd + 1 < totalPages) {
        carryoverRight.push(currentRightEnd + 1);
        currentRightEnd += 1;
      }
    } else if (each > totalPagesIn - 1) {
      if (currentLeftEnd - 1 > 2) {
        carryoverLeft.unshift(currentLeftEnd - 1);
        currentLeftEnd -= 1;
      }
    } else {
      validPagination.push(each);
    }
  });
  const filteredPagination = [...carryoverLeft, ...validPagination, ...carryoverRight];
  const arrayLength = filteredPagination.length;
  if (totalPagesIn > leftOffset + rightOffset + 2) {
    if (filteredPagination[0] >= 4) {
      filteredPagination.splice(0, 1, "...");
    }
    if (filteredPagination[arrayLength - 1] <= totalPagesIn - 3) {
      filteredPagination.pop(1);
      filteredPagination.push("...");
    }
  }
  return filteredPagination;
};

const PaginationComponent = (props) => {
  const {
    selectedPage,
    onPageChange,
    totalCount,
    itemsPerPage,
  } = props;

  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const currentPage = parseInt(selectedPage, 10) + 1;
  const [pages, setPages] = useState([]);
  const [inputPage, setInputPage] = useState("");
  const [invalidPage, setInvalidPage] = useState(false);

  const handlePageChange = (page, e) => {
    e.preventDefault();
    setInvalidPage(false);
    if (page > 0 && page <= totalPages) {
      onPageChange(page - 1);
      if (inputPage !== page) {
        setInputPage("");
      }
    } else {
      setInvalidPage(true);
    }
  };

  const handleInputChange = (e) => {
    const { value } = e.target;
    setInvalidPage(false);
    const integerValue = parseInt(value, 10);
    if (integerValue > 0) {
      setInputPage(integerValue);
    } else if (value === "") {
      setInputPage("");
    }
  };

  const onKeyDown = (e) => {
    if (e.charCode === 13) {
      e.preventDefault();
      handlePageChange(inputPage, e);
    }
  };

  useEffect(() => {
    const paginationItems = generatePages(currentPage, totalPages);
    setPages(paginationItems);
  }, [totalPages, currentPage]);

  const getPageText = () => {
    const end = currentPage * itemsPerPage;
    const start = end + 1 - itemsPerPage;
    if (start === totalCount) {
      return (`Showing ${start} of`);
    }
    return (`Showing ${start} to  ${totalCount < end ? totalCount : end} of`);
  };

  return (
    totalPages > 1
      ? (
        <>
          <div className="show-count">
            {getPageText()}
            <span className="mx-1">{totalCount}</span>
            Results
          </div>
          <Pagination aria-label="Page navigation">
            <PaginationItem
              disabled={currentPage < 2}
              onClick={(e) => currentPage > 1 && handlePageChange(currentPage - 1, e)}
            >
              <PaginationLink>
                <i className="fal fa-angle-left"> </i>
              </PaginationLink>
            </PaginationItem>
            <PaginationItem
              active={currentPage === 1}
              onClick={(e) => currentPage !== 1 && handlePageChange(1, e)}
            >
              <PaginationLink>
                1
              </PaginationLink>
            </PaginationItem>
            <PaginationItem
              active={currentPage === 2}
              onClick={(e) => currentPage !== 2 && handlePageChange(2, e)}
            >
              <PaginationLink>
                2
              </PaginationLink>
            </PaginationItem>
            {pages && Array.isArray(pages) && pages.length > 0
              && pages.map((page, index) => (

                <PaginationItem
                  key={`common-page-${index.toString()}`}
                  active={currentPage === page}
                  disabled={page === "..."}
                  onClick={(e) => page !== "..." && handlePageChange(page, e)}
                >
                  <PaginationLink>{page}</PaginationLink>
                </PaginationItem>
              ))}
            {pages[pages.length - 1] === "..." || pages[pages.length - 1] < totalPages - 1
              ? (
                <PaginationItem
                  active={currentPage === totalPages - 1}
                  onClick={(e) => handlePageChange(totalPages - 1, e)}
                >
                  <PaginationLink>
                    {totalPages - 1}
                  </PaginationLink>
                </PaginationItem>
              )
              : ""}
            {totalPages > 2
              ? (
                <PaginationItem
                  active={currentPage === totalPages}
                  onClick={(e) => handlePageChange(totalPages, e)}
                >
                  <PaginationLink>
                    {totalPages}
                  </PaginationLink>
                </PaginationItem>
              )
              : ""}
            <PaginationItem
              disabled={currentPage >= totalPages}
              onClick={(e) => (currentPage < totalPages && handlePageChange(currentPage + 1, e))}
            >
              <PaginationLink>
                <i className="fal fa-angle-right"> </i>
              </PaginationLink>
            </PaginationItem>
          </Pagination>

          <div className="goto-page">
            <h5>Go to page</h5>
            <Input
              type="text"
              name="inputPage"
              value={inputPage}
              onChange={handleInputChange}
              invalid={invalidPage}
              onFocus={() => setInvalidPage(false)}
              onKeyPress={onKeyDown}
            />
            <div className="goto-msg">
              Enter a valid page number
            </div>
            <Button color="transparent" className="item-center" onClick={(e) => handlePageChange(inputPage, e)}>
              <i className="fal fa-angle-right ms-1"> </i>
            </Button>
          </div>
        </>
      )
      : ""
  );
};

PaginationComponent.propTypes = {
  selectedPage: PropTypes.number.isRequired,
  totalCount: PropTypes.number.isRequired,
  itemsPerPage: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
};

export default PaginationComponent;
